import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faLink,
  faUnlink,
  faSync,
  faExclamationCircle,
  faCheckCircle,
  faArrowUp,
  faArrowDown,
  faSort,
  faSortUp,
  faSortDown,
  faUniversity,
  faLayerGroup,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { schwabApi } from '../api/schwabApi';
import { useAuth } from '../contexts/AuthContext';
import SchwabOrdersPanel from '../components/SchwabOrdersPanel';
import SchwabTransactionsPanel from '../components/SchwabTransactionsPanel';
import SchwabPreferencesPanel from '../components/SchwabPreferencesPanel';

const VIEW_STORAGE_KEY = 'schwabViewMode';
const ACCOUNT_STORAGE_KEY = 'schwabSelectedAccount';
const TAB_STORAGE_KEY = 'schwabActiveTab';

const TABS = [
  { id: 'positions', label: 'Positions' },
  { id: 'orders', label: 'Orders' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'preferences', label: 'Preferences' },
];

function formatCurrency(value) {
  const n = Number(value);
  const normalized = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(normalized);
}

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: n % 1 === 0 ? 0 : 4,
  }).format(n);
}

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const pct = Math.abs(n) <= 1 ? n * 100 : n;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
}

function maskAccount(num) {
  if (!num) return '—';
  const s = String(num);
  if (s.length <= 4) return s;
  return `••••${s.slice(-4)}`;
}

function accountLabel(acct, nickByNumber = {}) {
  if (!acct) return 'Account';
  const nick = nickByNumber[String(acct.accountNumber)];
  if (nick) return `${nick} (${maskAccount(acct.accountNumber)})`;
  const type = acct.accountType || 'Account';
  return `${type} ${maskAccount(acct.accountNumber)}`;
}

function sumField(rows, field) {
  return rows.reduce((s, r) => s + (Number(r[field]) || 0), 0);
}

function isLongPosition(pos) {
  return String(pos?.side || '').toLowerCase() !== 'short';
}

function positionAssetType(pos) {
  return String(pos?.assetType || 'EQUITY').toUpperCase() === 'OPTION' ? 'OPTION' : 'EQUITY';
}

function positionQtyAbs(pos) {
  return Math.abs(Number(pos?.qty)) || 0;
}

/** Prefill instruction for Buy / Sell / Close from a position row. */
function instructionForAction(pos, action) {
  const option = positionAssetType(pos) === 'OPTION';
  const long = isLongPosition(pos);
  if (action === 'close') {
    if (option) return long ? 'SELL_TO_CLOSE' : 'BUY_TO_CLOSE';
    return long ? 'SELL' : 'BUY_TO_COVER';
  }
  if (action === 'buy') {
    if (option) return long ? 'BUY_TO_OPEN' : 'BUY_TO_CLOSE';
    return long ? 'BUY' : 'BUY_TO_COVER';
  }
  // sell
  if (option) return long ? 'SELL_TO_CLOSE' : 'SELL_TO_OPEN';
  return long ? 'SELL' : 'SELL_SHORT';
}

function positionActionKey(pos, action) {
  return `${action}-${pos.accountNumber}-${pos.symbol}-${pos.side}-${pos.assetType}`;
}

const SchwabPage = () => {
  const { maskAmounts } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [sortColumn, setSortColumn] = useState('marketValue');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_STORAGE_KEY) || 'all');
  const [selectedAccount, setSelectedAccount] = useState(
    () => localStorage.getItem(ACCOUNT_STORAGE_KEY) || ''
  );
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem(TAB_STORAGE_KEY) || 'positions');
  const [accountFilter, setAccountFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [ticketDraft, setTicketDraft] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [actionMessage, setActionMessage] = useState(null);

  const money = (v) => (maskAmounts ? '••••' : formatCurrency(v));

  const loadAll = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      else setRefreshing(true);
      setError(null);

      const statusRes = await schwabApi.getStatus();
      const nextStatus = statusRes.data;
      setStatus(nextStatus);

      if (nextStatus?.connected) {
        const [summaryRes, positionsRes, prefRes] = await Promise.all([
          schwabApi.getSummary(),
          schwabApi.getPositions(),
          schwabApi.getUserPreference().catch(() => ({ data: null })),
        ]);
        setSummary(summaryRes.data);
        setPositions(positionsRes.data || []);
        setPreferences(prefRes.data || null);
      } else {
        setSummary(null);
        setPositions([]);
        setPreferences(null);
      }
    } catch (err) {
      const code = err.response?.data?.code;
      const message = err.response?.data?.error || err.message;
      if (code === 'not_connected' || code === 'reauth_required') {
        setStatus((prev) => ({
          ...(prev || {}),
          connected: false,
          needsReauth: code === 'reauth_required',
          configured: prev?.configured ?? true,
        }));
        setSummary(null);
        setPositions([]);
        setPreferences(null);
      }
      setError(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const result = searchParams.get('schwab');
    if (!result) return;
    const message = searchParams.get('message');
    if (result === 'connected') {
      setBanner({ tone: 'bull', text: 'Schwab account connected.' });
      loadAll(false);
    } else if (result === 'error') {
      setBanner({
        tone: 'bear',
        text: message || 'Schwab connection failed.',
      });
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, loadAll]);

  const nickByNumber = useMemo(() => {
    const map = {};
    for (const a of preferences?.accounts || []) {
      if (a.accountNumber && a.nickName) map[String(a.accountNumber)] = a.nickName;
    }
    return map;
  }, [preferences]);

  const accounts = useMemo(() => {
    const list = [...(summary?.accounts || [])];
    list.sort((a, b) => (Number(b.equity) || 0) - (Number(a.equity) || 0));
    return list;
  }, [summary]);

  const labelFor = useCallback(
    (acct) => accountLabel(acct, nickByNumber),
    [nickByNumber]
  );

  const setTab = (id) => {
    setActiveTab(id);
    localStorage.setItem(TAB_STORAGE_KEY, id);
  };

  const openQuickTicket = useCallback(
    (pos, action) => {
      const qty = positionQtyAbs(pos);
      setTicketDraft({
        id: `${Date.now()}-${pos.symbol}-${action}`,
        accountNumber: String(pos.accountNumber || ''),
        symbol: String(pos.symbol || '').trim(),
        instruction: instructionForAction(pos, action),
        quantity: qty ? String(qty) : '',
        orderType: 'MARKET',
        price: '',
        stopPrice: '',
        duration: 'DAY',
        session: 'NORMAL',
        assetType: positionAssetType(pos),
      });
      setTab('orders');
      setActionMessage(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleClosePosition = useCallback(
    async (pos) => {
      const qty = positionQtyAbs(pos);
      if (!qty) return;
      const acct =
        nickByNumber[String(pos.accountNumber)] || maskAccount(pos.accountNumber);
      const instruction = instructionForAction(pos, 'close');
      if (
        !window.confirm(
          `Close ${pos.symbol} · ${formatQty(qty)} on ${acct}?\n\nMarket ${instruction} will be sent to Schwab.`
        )
      ) {
        return;
      }
      const key = positionActionKey(pos, 'close');
      try {
        setActionLoading((prev) => ({ ...prev, [key]: true }));
        setActionMessage(null);
        const res = await schwabApi.placeOrder({
          accountNumber: pos.accountNumber,
          symbol: pos.symbol,
          instruction,
          quantity: qty,
          orderType: 'MARKET',
          duration: 'DAY',
          session: 'NORMAL',
          assetType: positionAssetType(pos),
        });
        setActionMessage({
          tone: 'bull',
          text: res.data?.orderId
            ? `Close submitted for ${pos.symbol} (#${res.data.orderId})`
            : `Close submitted for ${pos.symbol}`,
        });
        await loadAll(false);
      } catch (err) {
        setActionMessage({
          tone: 'bear',
          text: err.response?.data?.error || err.message || 'Close failed',
        });
      } finally {
        setActionLoading((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [loadAll, nickByNumber]
  );

  const renderPositionActions = (pos) => {
    const closeKey = positionActionKey(pos, 'close');
    const closing = Boolean(actionLoading[closeKey]);
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => openQuickTicket(pos, 'buy')}
          className="text-[10px] px-2 py-1 rounded border border-hairline text-bull hover:bg-elevated"
          title="Open ticket to buy / cover"
        >
          Buy
        </button>
        <button
          type="button"
          onClick={() => openQuickTicket(pos, 'sell')}
          className="text-[10px] px-2 py-1 rounded border border-hairline text-bear hover:bg-elevated"
          title="Open ticket to sell / short"
        >
          Sell
        </button>
        <button
          type="button"
          onClick={() => handleClosePosition(pos)}
          disabled={closing}
          className="text-[10px] px-2 py-1 rounded border border-hairline text-ink hover:bg-elevated disabled:opacity-50"
          title="Market close full position"
        >
          {closing ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Close'}
        </button>
      </div>
    );
  };

  const positionCountByAccount = useMemo(() => {
    const map = {};
    for (const p of positions) {
      const key = p.accountNumber || '';
      map[key] = (map[key] || 0) + 1;
    }
    return map;
  }, [positions]);

  // Keep selected account valid when data loads / accounts change
  useEffect(() => {
    if (!accounts.length) return;
    if (viewMode === 'single' && selectedAccount) {
      const stillExists = accounts.some((a) => a.accountNumber === selectedAccount);
      if (!stillExists) {
        const next = accounts[0].accountNumber;
        setSelectedAccount(next);
        localStorage.setItem(ACCOUNT_STORAGE_KEY, next);
      }
    } else if (viewMode === 'single' && !selectedAccount) {
      const next = accounts[0].accountNumber;
      setSelectedAccount(next);
      localStorage.setItem(ACCOUNT_STORAGE_KEY, next);
    }
  }, [accounts, viewMode, selectedAccount]);

  const setMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_STORAGE_KEY, mode);
    if (mode === 'single' && !selectedAccount && accounts[0]) {
      const next = accounts[0].accountNumber;
      setSelectedAccount(next);
      localStorage.setItem(ACCOUNT_STORAGE_KEY, next);
    }
  };

  const selectAccount = (accountNumber) => {
    setSelectedAccount(accountNumber);
    localStorage.setItem(ACCOUNT_STORAGE_KEY, accountNumber);
    if (viewMode !== 'single') {
      setViewMode('single');
      localStorage.setItem(VIEW_STORAGE_KEY, 'single');
    }
  };

  const filteredAccounts = useMemo(() => {
    const q = accountFilter.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => {
      const nick = nickByNumber[String(a.accountNumber)] || '';
      const hay = `${a.accountType || ''} ${a.accountNumber || ''} ${nick}`.toLowerCase();
      return hay.includes(q);
    });
  }, [accounts, accountFilter, nickByNumber]);

  const activeAccount = useMemo(() => {
    if (viewMode !== 'single') return null;
    return accounts.find((a) => a.accountNumber === selectedAccount) || null;
  }, [accounts, viewMode, selectedAccount]);

  const visiblePositions = useMemo(() => {
    let list =
      viewMode === 'single' && selectedAccount
        ? positions.filter((p) => p.accountNumber === selectedAccount)
        : [...positions];

    const q = positionFilter.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const hay = `${p.symbol || ''} ${p.assetType || ''} ${p.description || ''} ${p.accountNumber || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [positions, viewMode, selectedAccount, positionFilter]);

  const sortedPositions = useMemo(() => {
    const list = [...visiblePositions];
    list.sort((a, b) => {
      let av = a[sortColumn];
      let bv = b[sortColumn];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av == null) av = 0;
      if (bv == null) bv = 0;
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [visiblePositions, sortColumn, sortDirection]);

  const metrics = useMemo(() => {
    if (viewMode === 'single' && activeAccount) {
      const acctPositions = positions.filter((p) => p.accountNumber === activeAccount.accountNumber);
      const equity = Number(activeAccount.equity) || 0;
      const lastEquity = Number(activeAccount.lastEquity) || 0;
      const accountDayPl =
        activeAccount.dayPl != null
          ? Number(activeAccount.dayPl)
          : lastEquity
            ? equity - lastEquity
            : sumField(acctPositions, 'unrealizedIntradayPl');
      return {
        equity,
        cash: activeAccount.cash,
        buyingPower: activeAccount.buyingPower,
        dayPl: accountDayPl,
        openDayPl: sumField(acctPositions, 'unrealizedIntradayPl'),
        unrealizedPl: sumField(acctPositions, 'unrealizedPl'),
        marketValue: sumField(acctPositions, 'marketValue'),
        positionCount: acctPositions.length,
        label: labelFor(activeAccount),
      };
    }
    const openDayPl = sumField(positions, 'unrealizedIntradayPl');
    const accountDayPl =
      summary?.dayPl != null
        ? Number(summary.dayPl)
        : summary?.lastEquity
          ? (Number(summary.equity) || 0) - (Number(summary.lastEquity) || 0)
          : openDayPl;
    return {
      equity: summary?.equity,
      cash: summary?.cash,
      buyingPower: summary?.buyingPower,
      dayPl: accountDayPl,
      openDayPl,
      unrealizedPl: sumField(positions, 'unrealizedPl'),
      marketValue: sumField(positions, 'marketValue'),
      positionCount: positions.length,
      label: `All accounts (${accounts.length})`,
    };
  }, [viewMode, activeAccount, summary, positions, accounts.length, labelFor]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      const res = await schwabApi.startOAuth();
      const url = res.data?.url;
      if (!url) throw new Error('No authorization URL returned');
      window.location.href = url;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Schwab from this app? You can reconnect anytime.')) {
      return;
    }
    try {
      setDisconnecting(true);
      await schwabApi.disconnect();
      setBanner({ tone: 'muted', text: 'Schwab disconnected.' });
      await loadAll(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(column === 'symbol' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (column) => {
    if (sortColumn !== column) {
      return <FontAwesomeIcon icon={faSort} className="text-muted text-[8px] ml-1" />;
    }
    return sortDirection === 'asc' ? (
      <FontAwesomeIcon icon={faSortUp} className="text-action text-[8px] ml-1" />
    ) : (
      <FontAwesomeIcon icon={faSortDown} className="text-action text-[8px] ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-24">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-muted" />
      </div>
    );
  }

  return (
    <main className="flex-1 w-full min-w-0 px-3 sm:px-4 lg:px-6 py-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted mb-0.5">Brokers</div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-semibold text-ink flex items-center gap-2">
              <FontAwesomeIcon icon={faUniversity} className="text-action text-base" />
              Schwab
            </h1>
            {status?.connected && (
              <span className="text-xs text-muted">
                Viewing <span className="text-ink">{metrics.label}</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
          {status?.connected && accounts.length > 0 && (
            <div className="inline-flex rounded-md border border-hairline overflow-hidden text-xs flex-1 sm:flex-none">
              <button
                type="button"
                onClick={() => setMode('all')}
                className={`inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2 transition-colors ${
                  viewMode === 'all'
                    ? 'bg-action text-white'
                    : 'bg-canvas text-muted hover:text-ink hover:bg-elevated'
                }`}
              >
                <FontAwesomeIcon icon={faLayerGroup} />
                <span className="sm:hidden">All</span>
                <span className="hidden sm:inline">All accounts</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('single')}
                className={`inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2 border-l border-hairline transition-colors ${
                  viewMode === 'single'
                    ? 'bg-action text-white'
                    : 'bg-canvas text-muted hover:text-ink hover:bg-elevated'
                }`}
              >
                <span className="sm:hidden">One</span>
                <span className="hidden sm:inline">One account</span>
              </button>
            </div>
          )}

          {status?.connected && (
            <button
              type="button"
              onClick={() => loadAll(false)}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-hairline text-muted hover:text-ink hover:bg-elevated transition-colors"
              aria-label="Refresh"
            >
              <FontAwesomeIcon icon={faSync} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          )}
          {status?.connected ? (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-hairline text-bear hover:bg-elevated transition-colors"
              aria-label="Disconnect Schwab"
            >
              <FontAwesomeIcon
                icon={disconnecting ? faSpinner : faUnlink}
                className={disconnecting ? 'animate-spin' : ''}
              />
              <span className="hidden sm:inline">Disconnect</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting || status?.configured === false}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-action text-white hover:opacity-90 transition-opacity disabled:opacity-50 w-full sm:w-auto justify-center"
            >
              <FontAwesomeIcon
                icon={connecting ? faSpinner : faLink}
                className={connecting ? 'animate-spin' : ''}
              />
              Connect Schwab
            </button>
          )}
        </div>
      </div>

      {banner && (
        <div
          className={`flex items-start gap-2 px-4 py-3 rounded-md border border-hairline text-sm ${
            banner.tone === 'bull'
              ? 'text-bull bg-elevated'
              : banner.tone === 'bear'
                ? 'text-bear bg-elevated'
                : 'text-muted bg-elevated'
          }`}
        >
          <FontAwesomeIcon
            icon={banner.tone === 'bear' ? faExclamationCircle : faCheckCircle}
            className="mt-0.5"
          />
          <span>{banner.text}</span>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-md border border-hairline text-sm text-bear bg-elevated">
          <FontAwesomeIcon icon={faExclamationCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {status?.configured === false && (
        <div className="lt-card px-5 py-4 space-y-2">
          <h2 className="text-sm font-medium text-ink">Schwab API not configured</h2>
          <p className="text-sm text-muted">
            Add these to your server environment (and register the same callback URL in the Schwab
            Developer Portal):
          </p>
          <ul className="text-xs font-mono text-muted space-y-1 list-disc list-inside">
            <li>SCHWAB_CLIENT_ID</li>
            <li>SCHWAB_CLIENT_SECRET</li>
            <li>SCHWAB_REDIRECT_URI (HTTPS, e.g. https://your-host/api/schwab/oauth/callback)</li>
          </ul>
        </div>
      )}

      {status?.configured !== false && !status?.connected && (
        <div className="lt-card px-5 py-8 text-center space-y-3">
          <p className="text-sm text-muted max-w-md mx-auto">
            {status?.needsReauth
              ? 'Your Schwab refresh token expired (7-day lifetime). Reconnect to continue.'
              : 'Authorize this app via Schwab OAuth to view live balances and positions from your Schwab brokerage account.'}
          </p>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-action text-white hover:opacity-90 disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={connecting ? faSpinner : faLink}
              className={connecting ? 'animate-spin' : ''}
            />
            {status?.needsReauth ? 'Reconnect Schwab' : 'Connect Schwab'}
          </button>
        </div>
      )}

      {status?.connected && summary && (
        <>
          {/* Metrics — scoped to All or selected account */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3">
            {[
              { label: 'Equity', value: money(metrics.equity), tone: 'ink' },
              { label: 'Cash', value: money(metrics.cash), tone: 'ink' },
              { label: 'Buying power', value: money(metrics.buyingPower), tone: 'ink' },
              {
                label: 'Day P/L',
                value: money(metrics.dayPl),
                tone: (metrics.dayPl || 0) >= 0 ? 'bull' : 'bear',
                hint: 'Account value change today (incl. realized)',
              },
              {
                label: 'Unrealized',
                value: money(metrics.unrealizedPl),
                tone: (metrics.unrealizedPl || 0) >= 0 ? 'bull' : 'bear',
              },
              {
                label: 'Positions',
                value: String(metrics.positionCount ?? 0),
                tone: 'ink',
              },
            ].map((m) => (
              <div key={m.label} className="lt-card px-3 sm:px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted">{m.label}</div>
                <div
                  className={`num text-base sm:text-lg mt-1 ${
                    m.tone === 'bull'
                      ? 'text-bull'
                      : m.tone === 'bear'
                        ? 'text-bear'
                        : 'text-ink'
                  }`}
                >
                  {m.value}
                </div>
                {m.hint && (
                  <div className="text-[10px] text-muted mt-0.5 leading-snug">{m.hint}</div>
                )}
              </div>
            ))}
          </div>

          {/* Account picker — full width scrollable strip */}
          {accounts.length > 0 && (
            <div className="lt-card overflow-hidden">
              <div className="px-3 sm:px-4 py-2.5 border-b border-hairline flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-ink">
                  Accounts
                  <span className="text-muted font-normal ml-2 num text-xs">
                    {filteredAccounts.length}
                    {accountFilter ? ` / ${accounts.length}` : ''}
                  </span>
                </div>
                <div className="relative w-full sm:w-auto">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-muted"
                  />
                  <input
                    type="search"
                    value={accountFilter}
                    onChange={(e) => setAccountFilter(e.target.value)}
                    placeholder="Filter accounts…"
                    className="w-full sm:w-56 pl-7 pr-2 py-1.5 text-xs rounded-md border border-hairline bg-canvas text-ink placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-action"
                  />
                </div>
              </div>

              <div className="p-2 sm:p-3 flex gap-2 overflow-x-auto snap-x snap-mandatory [-webkit-overflow-scrolling:touch]">
                <button
                  type="button"
                  onClick={() => setMode('all')}
                  className={`shrink-0 snap-start min-w-[132px] sm:min-w-[140px] text-left px-3 py-2.5 rounded-md border transition-colors ${
                    viewMode === 'all'
                      ? 'border-action bg-elevated'
                      : 'border-hairline hover:bg-elevated'
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted">Aggregate</div>
                  <div className="text-sm font-medium text-ink mt-0.5">All accounts</div>
                  <div className="num text-xs text-muted mt-1">{money(summary.equity)}</div>
                  <div className="text-[10px] text-muted mt-0.5">
                    {positions.length} pos · {accounts.length} accts
                  </div>
                </button>

                {filteredAccounts.map((acct) => {
                  const selected =
                    viewMode === 'single' && selectedAccount === acct.accountNumber;
                  const posCount = positionCountByAccount[acct.accountNumber] || 0;
                  const nick = nickByNumber[String(acct.accountNumber)];
                  return (
                    <button
                      key={acct.accountNumber || acct.accountType}
                      type="button"
                      onClick={() => selectAccount(acct.accountNumber)}
                      className={`shrink-0 snap-start min-w-[140px] max-w-[180px] sm:min-w-[150px] sm:max-w-[200px] text-left px-3 py-2.5 rounded-md border transition-colors ${
                        selected
                          ? 'border-action bg-elevated'
                          : 'border-hairline hover:bg-elevated'
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.12em] text-muted truncate">
                        {nick || acct.accountType || 'Account'}
                      </div>
                      <div className="text-sm font-medium text-ink mt-0.5 num truncate">
                        {maskAccount(acct.accountNumber)}
                      </div>
                      <div className="num text-xs text-ink mt-1">{money(acct.equity)}</div>
                      <div className="text-[10px] text-muted mt-0.5 flex justify-between gap-2">
                        <span>{posCount} pos</span>
                        <span
                          className={
                            (acct.cash || 0) >= 0 ? 'text-muted' : 'text-bear'
                          }
                        >
                          cash {money(acct.cash)}
                        </span>
                      </div>
                    </button>
                  );
                })}

                {filteredAccounts.length === 0 && (
                  <div className="px-3 py-4 text-sm text-muted">No accounts match that filter.</div>
                )}
              </div>
            </div>
          )}

          {/* Main tabs */}
          <div className="overflow-x-auto [-webkit-overflow-scrolling:touch] border-b border-hairline -mx-3 px-3 sm:mx-0 sm:px-0">
            <div className="flex gap-1 min-w-max">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTab(tab.id)}
                  className={`px-3 sm:px-4 py-2.5 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? 'border-action text-ink font-medium'
                      : 'border-transparent text-muted hover:text-ink'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'positions' && (
          <div className="lt-card overflow-hidden">
            <div className="px-3 sm:px-4 py-2.5 border-b border-hairline flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-ink">
                Positions
                <span className="text-muted font-normal ml-2 num text-xs">
                  {sortedPositions.length}
                  {positionFilter || viewMode === 'single'
                    ? ` shown · ${metrics.positionCount} in scope`
                    : ''}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <div className="text-xs text-muted flex flex-wrap gap-x-3 gap-y-1">
                  <span>
                    MV <span className="num text-ink">{money(sumField(visiblePositions, 'marketValue'))}</span>
                  </span>
                  <span>
                    Day{' '}
                    <span
                      className={`num ${
                        sumField(visiblePositions, 'unrealizedIntradayPl') >= 0
                          ? 'text-bull'
                          : 'text-bear'
                      }`}
                    >
                      {money(sumField(visiblePositions, 'unrealizedIntradayPl'))}
                    </span>
                  </span>
                  <span>
                    Unrealized{' '}
                    <span
                      className={`num ${
                        sumField(visiblePositions, 'unrealizedPl') >= 0 ? 'text-bull' : 'text-bear'
                      }`}
                    >
                      {money(sumField(visiblePositions, 'unrealizedPl'))}
                    </span>
                  </span>
                </div>
                <div className="relative w-full sm:w-auto">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-muted"
                  />
                  <input
                    type="search"
                    value={positionFilter}
                    onChange={(e) => setPositionFilter(e.target.value)}
                    placeholder="Filter symbols…"
                    className="w-full sm:w-52 pl-7 pr-2 py-1.5 text-xs rounded-md border border-hairline bg-canvas text-ink placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-action"
                  />
                </div>
              </div>
            </div>

            {actionMessage && (
              <div
                className={`mx-3 mb-2 flex items-start gap-2 px-3 py-2 rounded-md border border-hairline text-sm ${
                  actionMessage.tone === 'bull' ? 'text-bull bg-canvas' : 'text-bear bg-canvas'
                }`}
              >
                <FontAwesomeIcon
                  icon={actionMessage.tone === 'bull' ? faCheckCircle : faExclamationCircle}
                  className="mt-0.5 shrink-0"
                />
                <span className="flex-1">{actionMessage.text}</span>
                <button
                  type="button"
                  onClick={() => setActionMessage(null)}
                  className="text-muted hover:text-ink text-xs"
                >
                  Dismiss
                </button>
              </div>
            )}

            {sortedPositions.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted">
                {positionFilter
                  ? 'No positions match that filter.'
                  : viewMode === 'single'
                    ? 'No open positions in this account.'
                    : 'No open positions.'}
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-hairline">
                  {sortedPositions.map((pos) => {
                    const pl = Number(pos.unrealizedPl) || 0;
                    const day = Number(pos.unrealizedIntradayPl) || 0;
                    return (
                      <div
                        key={`${pos.accountNumber}-${pos.symbol}-${pos.side}-${pos.assetType}`}
                        className="px-3 py-3 space-y-2"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <Link
                              to={`/asset/${encodeURIComponent(
                                String(pos.symbol || '').split(' ')[0]
                              )}`}
                              className="font-semibold text-action hover:underline"
                            >
                              {pos.symbol}
                            </Link>
                            <div className="text-[10px] text-muted mt-0.5">
                              {pos.assetType}
                              {pos.side === 'short' ? ' · short' : ''}
                              {viewMode === 'all'
                                ? ` · ${
                                    nickByNumber[String(pos.accountNumber)] ||
                                    maskAccount(pos.accountNumber)
                                  }`
                                : ''}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="num text-sm text-ink">{money(pos.marketValue)}</div>
                            <div
                              className={`num text-xs ${pl >= 0 ? 'text-bull' : 'text-bear'}`}
                            >
                              {money(pl)} ({formatPercent(pos.unrealizedPlpc)})
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-[11px]">
                          <div>
                            <div className="text-muted uppercase tracking-wide text-[9px]">Qty</div>
                            <div className="num text-ink">{formatQty(pos.qty)}</div>
                          </div>
                          <div>
                            <div className="text-muted uppercase tracking-wide text-[9px]">Price</div>
                            <div className="num text-ink">{money(pos.currentPrice)}</div>
                          </div>
                          <div>
                            <div className="text-muted uppercase tracking-wide text-[9px]">Day</div>
                            <div className={`num ${day >= 0 ? 'text-bull' : 'text-bear'}`}>
                              {money(day)}
                            </div>
                          </div>
                        </div>
                        <div className="pt-1">{renderPositionActions(pos)}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm min-w-[980px]">
                    <thead className="sticky top-0 bg-elevated/95 backdrop-blur-sm z-10">
                      <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted border-b border-hairline">
                        {[
                          ['symbol', 'Symbol'],
                          ['qty', 'Qty'],
                          ['currentPrice', 'Price'],
                          ['marketValue', 'Market value'],
                          ['costBasis', 'Cost'],
                          ['averagePrice', 'Avg'],
                          ['unrealizedPl', 'Unrealized'],
                          ['unrealizedIntradayPl', 'Day P/L'],
                          ...(viewMode === 'all' ? [['accountNumber', 'Account']] : []),
                        ].map(([col, label]) => (
                          <th key={col} className="px-3 py-2 font-medium whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleSort(col)}
                              className="inline-flex items-center hover:text-ink"
                            >
                              {label}
                              {sortIcon(col)}
                            </button>
                          </th>
                        ))}
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPositions.map((pos) => {
                        const pl = Number(pos.unrealizedPl) || 0;
                        const day = Number(pos.unrealizedIntradayPl) || 0;
                        return (
                          <tr
                            key={`${pos.accountNumber}-${pos.symbol}-${pos.side}-${pos.assetType}`}
                            className="border-b border-hairline last:border-0 hover:bg-elevated/50"
                          >
                            <td className="px-3 py-2">
                              <Link
                                to={`/asset/${encodeURIComponent(
                                  String(pos.symbol || '').split(' ')[0]
                                )}`}
                                className="font-medium text-action hover:underline"
                              >
                                {pos.symbol}
                              </Link>
                              <div className="text-[10px] text-muted">
                                {pos.assetType}
                                {pos.side === 'short' ? ' · short' : ''}
                              </div>
                            </td>
                            <td className="px-3 py-2 num whitespace-nowrap">{formatQty(pos.qty)}</td>
                            <td className="px-3 py-2 num whitespace-nowrap">{money(pos.currentPrice)}</td>
                            <td className="px-3 py-2 num whitespace-nowrap">{money(pos.marketValue)}</td>
                            <td className="px-3 py-2 num whitespace-nowrap">{money(pos.costBasis)}</td>
                            <td className="px-3 py-2 num whitespace-nowrap">{money(pos.averagePrice)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div
                                className={`num inline-flex items-center gap-1 ${
                                  pl >= 0 ? 'text-bull' : 'text-bear'
                                }`}
                              >
                                <FontAwesomeIcon
                                  icon={pl >= 0 ? faArrowUp : faArrowDown}
                                  className="text-[9px]"
                                />
                                {money(pl)}
                              </div>
                              <div className="text-[10px] text-muted num">
                                {formatPercent(pos.unrealizedPlpc)}
                              </div>
                            </td>
                            <td
                              className={`px-3 py-2 num whitespace-nowrap ${
                                day >= 0 ? 'text-bull' : 'text-bear'
                              }`}
                            >
                              {money(day)}
                            </td>
                            {viewMode === 'all' && (
                              <td className="px-3 py-2 text-xs text-muted">
                                <button
                                  type="button"
                                  onClick={() => selectAccount(pos.accountNumber)}
                                  className="num hover:text-action text-left"
                                  title="View this account only"
                                >
                                  {nickByNumber[String(pos.accountNumber)] ||
                                    maskAccount(pos.accountNumber)}
                                </button>
                                <div className="text-[10px]">
                                  {maskAccount(pos.accountNumber)}
                                  {pos.accountType ? ` · ${pos.accountType}` : ''}
                                </div>
                              </td>
                            )}
                            <td className="px-3 py-2 whitespace-nowrap">{renderPositionActions(pos)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          )}

          {activeTab === 'orders' && (
            <SchwabOrdersPanel
              accounts={accounts}
              viewMode={viewMode}
              selectedAccount={selectedAccount}
              accountLabel={labelFor}
              ticketDraft={ticketDraft}
              onTicketDraftConsumed={() => setTicketDraft(null)}
            />
          )}

          {activeTab === 'transactions' && (
            <SchwabTransactionsPanel
              viewMode={viewMode}
              selectedAccount={selectedAccount}
            />
          )}

          {activeTab === 'preferences' && <SchwabPreferencesPanel />}

          {status.refreshTokenExpiresAt && (
            <p className="text-[11px] text-muted px-0.5">
              Refresh token expires{' '}
              <span className="num">
                {new Date(status.refreshTokenExpiresAt).toLocaleString()}
              </span>
              .
            </p>
          )}
        </>
      )}
    </main>
  );
};

export default SchwabPage;
